import React, { useCallback } from 'react';
import { createUseStyles } from 'react-jss';
import { GestureResponderEvent, StyleSheet } from 'react-native';
import { Button, Icon, Typography } from '@packages/ui/shared';
import { useNavigate } from 'react-router-dom';
import { useWorksheetState } from '@packages/contexts/worksheets';

export interface BreadcrumbModel {
    title: string;
    to?: string;
}

const useStyles = createUseStyles({
    header: {
        alignItems: 'center',
        backgroundColor: '#ffffff',
        borderBottom: '1px solid #e0e0e0',
        display: 'flex',
        justifyContent: 'space-between',
        padding: '16px 24px',
    },
    titleContainer: {
        alignItems: 'center',
        display: 'flex',
        gap: 8,
        maxWidth: '100%',
    },
    buttonsContainer: {
        alignItems: 'center',
        display: 'flex',
        gap: 16,
    },
});

const styles = StyleSheet.create({
    headerTitle: {
        fontSize: 21,
        fontWeight: '600',
        lineHeight: 32,
        overflow: 'hidden',
    },
    iconSpacing: {
        marginRight: 16,
    },
});

export interface AdminHubHeaderProps {
    externalButtons?: React.ReactNode[];
    breadcrumbs: BreadcrumbModel[];
    hideBackButton?: boolean;
}

export const AdminHubHeader: React.FC<AdminHubHeaderProps> = ({
    breadcrumbs,
    externalButtons = [],
    hideBackButton = false,
}) => {
    const classes = useStyles();
    const navigate = useNavigate();
    const { searchQuery } = useWorksheetState();

    const handleNavigation = useCallback(
        (event: GestureResponderEvent) => {
            event.preventDefault();
            if (breadcrumbs.length > 1) {
                const previousBreadcrumb = breadcrumbs[breadcrumbs.length - 2];
                if (previousBreadcrumb?.to) {
                    if (previousBreadcrumb.to === '/administration/worksheets' && searchQuery) {
                        navigate(`${previousBreadcrumb.to}?query=${encodeURIComponent(searchQuery)}`);
                        return;
                    }
                    navigate(previousBreadcrumb.to);
                    return;
                }
            }
            navigate(-1);
        },
        [breadcrumbs, navigate, searchQuery]
    );

    return (
        <div className={classes.header} data-testid="admin-hub-header">
            <div className={classes.titleContainer}>
                {!hideBackButton && breadcrumbs.length > 1 && (
                    <Button
                        accessibilityLabel="arrow-back"
                        accessibilityRole="button"
                        onPress={handleNavigation}
                        style={styles.iconSpacing}
                        variant="ghost-blue"
                    >
                        <Icon name="arrow-back" color="textDarkSecondary" />
                    </Button>
                )}
                {breadcrumbs.map((breadcrumb, index) => (
                    <React.Fragment key={index}>
                        <Typography
                            color={index === breadcrumbs.length - 1 ? 'black' : 'textDarkSecondary'}
                            style={styles.headerTitle}
                            variant="h2"
                            numberOfLines={1}
                        >
                            {breadcrumb.title}
                        </Typography>
                        {index < breadcrumbs.length - 1 && <Icon name="chevron-right" color="textDarkSecondary" />}
                    </React.Fragment>
                ))}
            </div>
            {externalButtons.length > 0 && (
                <div className={classes.buttonsContainer}>
                    {externalButtons.map((button, index) => (
                        <React.Fragment key={index}>{button}</React.Fragment>
                    ))}
                </div>
            )}
        </div>
    );
};
